import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import { SimulationResult, ForecastType, MetricType } from './types';

const useStyles = makeStyles(theme => ({
  section: {
    padding: theme.spacing(3),
  },
  question: {
    fontStyle: 'italic',
    marginBottom: theme.spacing(2)
  },
  resultItem: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: theme.spacing(1, 0),
    padding: theme.spacing(1),
  },
}));

interface ResultsSectionProps {
  results: SimulationResult | null;
  forecastType: ForecastType;
  metricType: MetricType;
  numberOfSprints: string;
  numberOfItems: string;
  isCalculating: boolean;
  onGenerateForecast: () => void;
}

const ResultsSection: React.FC<ResultsSectionProps> = ({
  results,
  forecastType,
  metricType,
  numberOfSprints,
  numberOfItems,
  isCalculating,
  onGenerateForecast
}) => {
  const classes = useStyles();

  const getQuestionText = () => {
    const unit = metricType === 'throughput' ? 'items' : 'story points';
    if (forecastType === 'items-in-sprints') {
      return `How many ${unit} will we likely get done in ${numberOfSprints} sprints?`;
    } else {
      return `How many sprints will it take to complete ${numberOfItems} ${unit}?`;
    }
  };

  const getResultText = () => {
    const unitPlural = metricType === 'throughput' ? 'items' : 'story points';
    
    if (forecastType === 'items-in-sprints') {
      return `Based on ${results.simulations.toLocaleString()} simulations, here's what you can expect to complete in ${numberOfSprints} sprints:`;
    } else {
      return `Based on ${results.simulations.toLocaleString()} simulations, here's how many sprints it will take to complete ${numberOfItems} ${unitPlural}:`;
    }
  };

  const formatResultValue = (value: number, isSprints: boolean) => {
    const unit = isSprints ? 'sprints' : (metricType === 'throughput' ? 'items' : 'story points');
    return `${Math.round(value)} ${unit}`;
  };

  return (
    <Paper className={classes.section}>
      <Typography variant="h5" gutterBottom>
        Simulation Results
      </Typography>

      <Typography variant="body1" className={classes.question}>
        {getQuestionText()}
      </Typography>

      {results && (
        <>
          <Typography variant="body2" color="textSecondary" paragraph>
            {getResultText()}
          </Typography>
        
          
          <Box className={classes.resultItem}>
            <Typography>50% Confidence:</Typography>
            <Typography>
              {formatResultValue(results.percentiles.p50, forecastType === 'sprints-for-items')}
            </Typography>
          </Box>
          
          <Box className={classes.resultItem}>
            <Typography>
              80% Confidence
            </Typography>
            <Typography>
              {formatResultValue(results.percentiles.p80, forecastType === 'sprints-for-items')}
            </Typography>
          </Box>
          
          <Box className={classes.resultItem} style={{ backgroundColor: '#f0f0f0' }}>
            <Typography style={{ fontWeight: 'bold' }}>
              90% Confidence
            </Typography>
            <Typography style={{ fontWeight: 'bold' }}>
              {formatResultValue(results.percentiles.p90, forecastType === 'sprints-for-items')}
            </Typography>
          </Box>
          
          <Box className={classes.resultItem}>
            <Typography>
              99% Confidence
            </Typography>
            <Typography>
              {formatResultValue(results.percentiles.p99, forecastType === 'sprints-for-items')}
            </Typography>
           </Box>
           </>
       )}
       
       <Box my={3}>
         <Button 
           variant="contained" 
           color="primary" 
           fullWidth
           onClick={onGenerateForecast}
           disabled={isCalculating}
         >
           {isCalculating ? 'Calculating...' : 'Generate Forecast'}
         </Button>
       </Box>
    </Paper>
  );
};

export default ResultsSection;
